import {
    ActionIcon,
    Button,
    Checkbox,
    Group,
    NavLink,
    Paper,
    SegmentedControl,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import {useForm} from '@mantine/form';
import {useMediaQuery} from '@mantine/hooks';
import {closeModal, openModal} from '@mantine/modals';
import {notifications} from '@mantine/notifications';
import {IconDownload, IconFileTypePdf, IconPencil, IconPlus, IconTrash} from '@tabler/icons-react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useEffect, useLayoutEffect, useRef, useState, type RefObject} from 'react';
import {
    DocumentsService,
    type DocumentInfo,
    type DocumentSign,
    type DocumentSignCreate,
} from '../../client';
import {formatAdminDateTime} from '../../utils/formatDate';
import {formatRequestError} from '../../utils/formatRequestError';
import {TablerIconImage} from '../common/TablerIconImage';
import {PageLayout} from '../shell/PageLayout';
import {TablerIconPicker} from '../settings/TablerIconPicker';

/** Height from element top to its layout parent's content bottom; updates on resize. */
function useFillParentHeight(): {
    ref: RefObject<HTMLDivElement | null>;
    height: number | undefined;
} {
    const ref = useRef<HTMLDivElement | null>(null);
    const [height, setHeight] = useState<number | undefined>(undefined);

    useLayoutEffect(() => {
        const node = ref.current;
        if (!node) {
            return undefined;
        }

        const update = () => {
            const parent = node.parentElement;
            if (!parent) {
                return;
            }
            const top = node.getBoundingClientRect().top;
            const bottom = parent.getBoundingClientRect().bottom;
            setHeight(Math.max(200, Math.floor(bottom - top)));
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(document.documentElement);
        if (node.parentElement) {
            observer.observe(node.parentElement);
        }
        window.addEventListener('resize', update);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', update);
        };
    }, []);

    return {ref, height};
}

function formatModifiedAt(value: string | null): string {
    if (!value) {
        return 'Not generated yet';
    }
    return `Last modified: ${formatAdminDateTime(value)}`;
}

function downloadPdfBlob(blob: Blob, filename: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
}

function signLabel(sign: DocumentSign): string {
    return sign.title || sign.icon || 'Untitled';
}

type SignFormValues = {
    mode: 'title' | 'icon';
    title: string;
    icon: string;
    subtitle: string;
};

function SignForm({
    initial,
    onSubmit,
}: {
    initial: DocumentSignCreate;
    onSubmit: (values: DocumentSignCreate) => void;
}) {
    const form = useForm<SignFormValues>({
        initialValues: {
            mode: initial.icon ? 'icon' : 'title',
            title: initial.title ?? '',
            icon: initial.icon || 'info-circle',
            subtitle: initial.subtitle ?? '',
        },
        validate: {
            title: (value) => (!value.trim() ? 'Title is required' : null),
            icon: (value, values) => (
                values.mode === 'icon' && !value.trim() ? 'Icon is required' : null
            ),
        },
    });

    return (
        <form
            onSubmit={form.onSubmit((values) => {
                onSubmit(
                    values.mode === 'icon'
                        ? {title: values.title, icon: values.icon, subtitle: ''}
                        : {title: values.title, icon: '', subtitle: values.subtitle},
                );
            })}
        >
            <Stack>
                <SegmentedControl
                    data={[
                        {label: 'Title', value: 'title'},
                        {label: 'Icon', value: 'icon'},
                    ]}
                    {...form.getInputProps('mode')}
                />
                <TextInput
                    label={
                        form.values.mode === 'icon'
                            ? 'Title (filename & list only)'
                            : 'Title'
                    }
                    required
                    {...form.getInputProps('title')}
                />
                {form.values.mode === 'title' ? (
                    <TextInput
                        label="Subtitle (optional)"
                        {...form.getInputProps('subtitle')}
                    />
                ) : (
                    <TablerIconPicker
                        value={form.values.icon}
                        onChange={(icon) => form.setFieldValue('icon', icon)}
                    />
                )}
                <Group justify="flex-end">
                    <Button type="submit">Save</Button>
                </Group>
            </Stack>
        </form>
    );
}

function DocumentListPanel({
    documents,
    selectedId,
    checkedIds,
    downloadLoading,
    onSelect,
    onToggleChecked,
    onToggleSelectAll,
    onDownloadOne,
    height,
}: {
    documents: DocumentInfo[];
    selectedId: string | null;
    checkedIds: Set<string>;
    downloadLoading: boolean;
    onSelect: (id: string) => void;
    onToggleChecked: (doc: DocumentInfo, checked: boolean) => void;
    onToggleSelectAll: (checked: boolean) => void;
    onDownloadOne: (doc: DocumentInfo) => void;
    height?: number;
}) {
    const generatedDocuments = documents.filter((doc) => doc.modified_at);
    const allGeneratedSelected =
        generatedDocuments.length > 0
        && generatedDocuments.every((doc) => checkedIds.has(doc.id));
    const someGeneratedSelected = generatedDocuments.some((doc) => checkedIds.has(doc.id));

    const qrDocuments = documents.filter(
        (doc) => doc.id === 'website' || doc.id === 'kontodaten',
    );
    const signDocuments = documents.filter((doc) => doc.id.startsWith('sign-'));

    const renderDocumentRow = (doc: DocumentInfo) => {
        const generated = Boolean(doc.modified_at);
        return (
            <Group key={doc.id} gap="xs" wrap="nowrap" align="center">
                <Checkbox
                    aria-label={`Select ${doc.title} for download`}
                    checked={checkedIds.has(doc.id)}
                    disabled={!generated}
                    onChange={(event) => {
                        onToggleChecked(doc, event.currentTarget.checked);
                    }}
                />
                <NavLink
                    style={{flex: 1}}
                    label={doc.title}
                    description={formatModifiedAt(doc.modified_at)}
                    leftSection={<IconFileTypePdf size="1.1rem" stroke={1.5}/>}
                    active={selectedId === doc.id}
                    onClick={() => onSelect(doc.id)}
                    variant="light"
                />
                <ActionIcon
                    variant="subtle"
                    aria-label={`Download ${doc.title}`}
                    disabled={!generated || downloadLoading}
                    onClick={(event) => {
                        event.stopPropagation();
                        onDownloadOne(doc);
                    }}
                >
                    <IconDownload size="1.1rem" stroke={1.5}/>
                </ActionIcon>
            </Group>
        );
    };

    return (
        <Stack
            gap="xs"
            h={height}
            style={height ? {minHeight: height, overflow: 'auto'} : undefined}
        >
            <Group gap="xs" wrap="nowrap" align="center">
                <Checkbox
                    aria-label="Select all documents for download"
                    checked={allGeneratedSelected}
                    indeterminate={someGeneratedSelected && !allGeneratedSelected}
                    disabled={generatedDocuments.length === 0}
                    onChange={(event) => {
                        onToggleSelectAll(event.currentTarget.checked);
                    }}
                />
                <Title order={4}>Documents</Title>
            </Group>
            {documents.length === 0 ? (
                <Text c="dimmed">No documents yet.</Text>
            ) : (
                <>
                    <Stack gap="xs" mt="xs">
                        <Text size="sm" fw={600} c="dimmed">QR Codes</Text>
                        {qrDocuments.map(renderDocumentRow)}
                    </Stack>
                    {signDocuments.length > 0 ? (
                        <Stack gap="xs" mt="md">
                            <Text size="sm" fw={600} c="dimmed">Signs</Text>
                            {signDocuments.map(renderDocumentRow)}
                        </Stack>
                    ) : null}
                </>
            )}
        </Stack>
    );
}

function DocumentsPdfsGrid({
    documents,
    selectedId,
    checkedIds,
    downloadLoading,
    selected,
    previewUrl,
    previewLoading,
    emptyHint,
    onSelect,
    onToggleChecked,
    onToggleSelectAll,
    onDownloadOne,
}: {
    documents: DocumentInfo[];
    selectedId: string | null;
    checkedIds: Set<string>;
    downloadLoading: boolean;
    selected: DocumentInfo | null;
    previewUrl: string | null;
    previewLoading: boolean;
    emptyHint: string;
    onSelect: (id: string) => void;
    onToggleChecked: (doc: DocumentInfo, checked: boolean) => void;
    onToggleSelectAll: (checked: boolean) => void;
    onDownloadOne: (doc: DocumentInfo) => void;
}) {
    // Fill remaining height inside the viewport-sized PageLayout content area.
    // Only lock column heights on the two-column layout; stacked mobile keeps natural flow.
    const {ref, height} = useFillParentHeight();
    // Match SimpleGrid md (48em) so height lock and list scroll apply with two columns.
    const wide = useMediaQuery('(min-width: 48em)');
    const panelHeight = wide ? height : undefined;

    return (
        <SimpleGrid
            ref={ref}
            cols={{base: 1, md: 2}}
            spacing="lg"
            style={{flex: 1, minHeight: 0, width: '100%'}}
        >
            <DocumentListPanel
                documents={documents}
                selectedId={selectedId}
                checkedIds={checkedIds}
                downloadLoading={downloadLoading}
                onSelect={onSelect}
                onToggleChecked={onToggleChecked}
                onToggleSelectAll={onToggleSelectAll}
                onDownloadOne={onDownloadOne}
                height={panelHeight}
            />
            <DocumentPreviewPanel
                selected={selected}
                previewUrl={previewUrl}
                previewLoading={previewLoading}
                emptyHint={emptyHint}
                height={panelHeight}
            />
        </SimpleGrid>
    );
}

function DocumentPreviewPanel({
    selected,
    previewUrl,
    previewLoading,
    emptyHint,
    height,
}: {
    selected: DocumentInfo | null;
    previewUrl: string | null;
    previewLoading: boolean;
    emptyHint: string;
    height?: number;
}) {
    return (
        <Stack gap="xs" h={height} style={height ? {minHeight: height} : undefined}>
            <Group justify="space-between">
                <Title order={4}>Preview</Title>
                {selected ? (
                    <Text size="sm" c="dimmed">{selected.filename}</Text>
                ) : null}
            </Group>
            <Paper
                withBorder
                radius="md"
                p={0}
                style={{
                    overflow: 'hidden',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                {!selected?.modified_at ? (
                    <Stack align="center" justify="center" h="100%" gap="xs" p="md">
                        <Text c="dimmed" ta="center">
                            {emptyHint}
                        </Text>
                    </Stack>
                ) : previewLoading ? (
                    <Stack align="center" justify="center" h="100%">
                        <Text c="dimmed">Loading preview…</Text>
                    </Stack>
                ) : previewUrl ? (
                    <iframe
                        title={`${selected.title} preview`}
                        src={previewUrl}
                        style={{
                            border: 0,
                            width: '100%',
                            height: '100%',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Stack align="center" justify="center" h="100%">
                        <Text c="dimmed">Preview unavailable.</Text>
                    </Stack>
                )}
            </Paper>
        </Stack>
    );
}

export function Documents() {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<string | null>('pdfs');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);

    const {data, isLoading, isError} = useQuery({
        queryKey: ['documents'],
        queryFn: DocumentsService.getDocumentsApiV1DocumentsGet,
    });

    const {
        data: signsData,
        isLoading: signsLoading,
        isError: signsError,
    } = useQuery({
        queryKey: ['document-signs'],
        queryFn: DocumentsService.getDocumentSignsApiV1DocumentsSignsGet,
    });

    const documents = data?.documents ?? [];
    const signs = signsData?.signs ?? [];

    useEffect(() => {
        if (documents.length === 0) {
            setSelectedId(null);
            return;
        }
        if (!selectedId || !documents.some((doc) => doc.id === selectedId)) {
            const firstGenerated = documents.find((doc) => doc.modified_at) ?? documents[0];
            setSelectedId(firstGenerated.id);
        }
    }, [documents, selectedId]);

    useEffect(() => {
        setCheckedIds((prev) => {
            const available = new Set(
                documents.filter((doc) => doc.modified_at).map((doc) => doc.id),
            );
            const next = new Set([...prev].filter((id) => available.has(id)));
            if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
                return prev;
            }
            return next;
        });
    }, [documents]);

    const selectedModifiedAt = documents.find((doc) => doc.id === selectedId)?.modified_at ?? null;

    useEffect(() => {
        let cancelled = false;
        let objectUrl: string | null = null;

        const loadPreview = async () => {
            if (!selectedId || !selectedModifiedAt) {
                setPreviewUrl((prev) => {
                    if (prev) {
                        URL.revokeObjectURL(prev);
                    }
                    return null;
                });
                return;
            }

            setPreviewLoading(true);
            try {
                const blob = await DocumentsService.getDocumentFileBlob(selectedId);
                if (cancelled) {
                    return;
                }
                objectUrl = URL.createObjectURL(blob);
                setPreviewUrl((prev) => {
                    if (prev) {
                        URL.revokeObjectURL(prev);
                    }
                    return objectUrl;
                });
            } catch (error) {
                if (!cancelled) {
                    setPreviewUrl((prev) => {
                        if (prev) {
                            URL.revokeObjectURL(prev);
                        }
                        return null;
                    });
                    notifications.show({
                        color: 'red',
                        title: 'Could not load PDF preview',
                        message: formatRequestError(error, 'Request failed'),
                    });
                }
            } finally {
                if (!cancelled) {
                    setPreviewLoading(false);
                }
            }
        };

        void loadPreview();

        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [selectedId, selectedModifiedAt]);

    const generateMutation = useMutation({
        mutationFn: DocumentsService.generateDocumentsApiV1DocumentsGeneratePost,
        onSuccess: (response) => {
            queryClient.setQueryData(['documents'], response);
            notifications.show({
                color: 'green',
                title: 'PDFs generated',
                message: 'QR codes and signs were updated.',
            });
        },
        onError: (error) => {
            notifications.show({
                color: 'red',
                title: 'Could not generate PDFs',
                message: formatRequestError(error, 'Request failed'),
            });
        },
    });

    const invalidateSigns = () => {
        void queryClient.invalidateQueries({queryKey: ['document-signs']});
        void queryClient.invalidateQueries({queryKey: ['documents']});
    };

    const showError = (title: string, error: unknown) => {
        notifications.show({
            color: 'red',
            title,
            message: formatRequestError(error, 'Request failed'),
        });
    };

    const openSignModal = (sign?: DocumentSign) => {
        const initial: DocumentSignCreate = sign
            ? {title: sign.title, icon: sign.icon, subtitle: sign.subtitle}
            : {title: '', icon: '', subtitle: ''};
        openModal({
            modalId: 'documentSign',
            title: sign ? 'Edit sign' : 'Add sign',
            children: (
                <SignForm
                    initial={initial}
                    onSubmit={(values) => {
                        const request = sign
                            ? DocumentsService.updateDocumentSignApiV1DocumentsSignsSignIdPut({
                                signId: sign.id,
                                requestBody: values,
                            })
                            : DocumentsService.createDocumentSignApiV1DocumentsSignsPost({
                                requestBody: values,
                            });
                        request
                            .then(() => {
                                invalidateSigns();
                                closeModal('documentSign');
                                notifications.show({
                                    color: 'green',
                                    title: sign ? 'Sign updated' : 'Sign created',
                                    message: 'Click Generate PDFs to update the PDF files.',
                                });
                            })
                            .catch((error) => showError('Could not save sign', error));
                    }}
                />
            ),
        });
    };

    const confirmDeleteSign = (sign: DocumentSign) => {
        openModal({
            modalId: 'deleteDocumentSign',
            title: 'Delete sign',
            children: (
                <Stack>
                    <Text>
                        Delete sign “{signLabel(sign)}”? Generate PDFs again to remove its PDF file.
                    </Text>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => closeModal('deleteDocumentSign')}>
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            onClick={() => {
                                DocumentsService.deleteDocumentSignApiV1DocumentsSignsSignIdDelete({
                                    signId: sign.id,
                                })
                                    .then(() => {
                                        invalidateSigns();
                                        closeModal('deleteDocumentSign');
                                    })
                                    .catch((error) => showError('Could not delete sign', error));
                            }}
                        >
                            Delete
                        </Button>
                    </Group>
                </Stack>
            ),
        });
    };

    const selected = documents.find((doc) => doc.id === selectedId) ?? null;
    const onPdfsTab = activeTab === 'pdfs';
    const checkedDocuments = onPdfsTab
        ? documents.filter((doc) => checkedIds.has(doc.id) && doc.modified_at)
        : [];

    const toggleChecked = (doc: DocumentInfo, checked: boolean) => {
        if (!doc.modified_at) {
            return;
        }
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(doc.id);
            } else {
                next.delete(doc.id);
            }
            return next;
        });
    };

    const toggleSelectAllGenerated = (checked: boolean) => {
        const generated = documents.filter((doc) => doc.modified_at);
        if (checked) {
            setCheckedIds(new Set(generated.map((doc) => doc.id)));
        } else {
            setCheckedIds(new Set());
        }
    };

    const downloadDocuments = async (docs: DocumentInfo[]) => {
        const ready = docs.filter((doc) => doc.modified_at);
        if (ready.length === 0) {
            return;
        }
        setDownloadLoading(true);
        try {
            for (const doc of ready) {
                const blob = await DocumentsService.getDocumentFileBlob(doc.id);
                downloadPdfBlob(blob, doc.filename);
            }
        } catch (error) {
            notifications.show({
                color: 'red',
                title: 'Could not download PDF',
                message: formatRequestError(error, 'Request failed'),
            });
        } finally {
            setDownloadLoading(false);
        }
    };

    const emptyHint =
        'No PDF available yet. Configure bank details in Settings and add signs, then click Generate PDFs.';

    return (
        <PageLayout
            title="Documents"
            description="Generate and preview QR-code and sign PDFs."
            fillViewport
            actions={(
                <Group gap="sm">
                    <Button
                        variant="default"
                        leftSection={<IconDownload size="1rem" stroke={1.5}/>}
                        loading={downloadLoading}
                        disabled={checkedDocuments.length === 0 || downloadLoading}
                        onClick={() => void downloadDocuments(checkedDocuments)}
                    >
                        Download
                    </Button>
                    <Button
                        loading={generateMutation.isPending}
                        onClick={() => generateMutation.mutate()}
                    >
                        Generate PDFs
                    </Button>
                </Group>
            )}
        >
            {isLoading ? (
                <Text c="dimmed">Loading documents…</Text>
            ) : isError ? (
                <Text c="red">Could not load documents.</Text>
            ) : (
                <Tabs
                    value={activeTab}
                    onChange={setActiveTab}
                    style={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}
                >
                    <Tabs.List>
                        <Tabs.Tab value="pdfs">PDFs</Tabs.Tab>
                        <Tabs.Tab value="signs">Signs</Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel
                        value="pdfs"
                        pt="md"
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            display: activeTab === 'pdfs' ? 'flex' : undefined,
                            flexDirection: 'column',
                        }}
                    >
                        <DocumentsPdfsGrid
                            documents={documents}
                            selectedId={selectedId}
                            checkedIds={checkedIds}
                            downloadLoading={downloadLoading}
                            selected={selected}
                            previewUrl={previewUrl}
                            previewLoading={previewLoading}
                            emptyHint={emptyHint}
                            onSelect={setSelectedId}
                            onToggleChecked={toggleChecked}
                            onToggleSelectAll={toggleSelectAllGenerated}
                            onDownloadOne={(doc) => void downloadDocuments([doc])}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="signs" pt="md" style={{flex: 1, minHeight: 0, overflow: 'auto'}}>
                        <Stack gap="lg">
                            <Group justify="space-between" align="flex-start">
                                <Text size="sm" c="dimmed">
                                    Create title signs (optional subtitle) or icon signs with a
                                    label title used for the filename and list. Generate PDFs
                                    creates one landscape A4 file per sign in the PDFs tab.
                                </Text>
                                <Button
                                    leftSection={<IconPlus size={16}/>}
                                    onClick={() => openSignModal()}
                                >
                                    Add sign
                                </Button>
                            </Group>

                            {signsLoading ? (
                                <Text c="dimmed">Loading signs…</Text>
                            ) : signsError ? (
                                <Text c="red">Could not load signs.</Text>
                            ) : signs.length === 0 ? (
                                <Text c="dimmed">No signs yet. Click Add sign to create one.</Text>
                            ) : (
                                <Stack gap="xs">
                                    {signs.map((sign) => (
                                        <Paper key={sign.id} withBorder p="sm" radius="md">
                                            <Group justify="space-between" wrap="nowrap">
                                                <Stack gap={2} style={{minWidth: 0}}>
                                                    <Group gap="xs" wrap="nowrap">
                                                        {sign.icon ? (
                                                            <TablerIconImage
                                                                icon={sign.icon}
                                                                size={20}
                                                            />
                                                        ) : null}
                                                        <Text fw={600}>{signLabel(sign)}</Text>
                                                    </Group>
                                                    {!sign.icon && sign.subtitle ? (
                                                        <Text size="sm" c="dimmed">
                                                            {sign.subtitle}
                                                        </Text>
                                                    ) : null}
                                                </Stack>
                                                <Group gap="xs" wrap="nowrap">
                                                    <ActionIcon
                                                        variant="subtle"
                                                        aria-label={`Edit ${signLabel(sign)}`}
                                                        onClick={() => openSignModal(sign)}
                                                    >
                                                        <IconPencil size="1.1rem" stroke={1.5}/>
                                                    </ActionIcon>
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="red"
                                                        aria-label={`Delete ${signLabel(sign)}`}
                                                        onClick={() => confirmDeleteSign(sign)}
                                                    >
                                                        <IconTrash size="1.1rem" stroke={1.5}/>
                                                    </ActionIcon>
                                                </Group>
                                            </Group>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    </Tabs.Panel>
                </Tabs>
            )}
        </PageLayout>
    );
}
